import React, { useState, useEffect } from "react";
import {
  createItem,
  updateItem,
  getAllItems,
  deleteItem,
} from "../../firebase";
import { createOrUpdateStripeProductForItem } from "../../stripe";
import { Item } from "../../types";
import { useAuth } from "../../contexts/AuthContext";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ShoppingBagIcon,
  XMarkIcon,
  PhotoIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

const ProductManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    hidden: false,
  });
  const [formErrors, setFormErrors] = useState({
    name: "",
    description: "",
    price: "",
  });

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const fetchedItems = await getAllItems();
      setItems(fetchedItems);
      setError(null);
    } catch (err) {
      console.error("Failed to load items:", err);
      setError("Failed to load items. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item?: Item) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        name: item.name,
        description: item.description,
        price: item.price,
        hidden: item.hidden,
      });
      setPreviewUrl(item.imageURL || "");
    } else {
      setSelectedItem(null);
      setFormData({
        name: "",
        description: "",
        price: "",
        hidden: false,
      });
      setPreviewUrl("");
      setImage(null);
    }
    setFormErrors({
      name: "",
      description: "",
      price: "",
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImage(file);

      // Create preview URL
      const fileReader = new FileReader();
      fileReader.onload = () => {
        if (fileReader.result) {
          setPreviewUrl(fileReader.result.toString());
        }
      };
      fileReader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    let valid = true;
    const errors = {
      name: "",
      description: "",
      price: "",
    };

    if (!formData.name.trim()) {
      errors.name = "Name is required";
      valid = false;
    }

    if (!formData.description.trim()) {
      errors.description = "Description is required";
      valid = false;
    }

    if (!formData.price.trim()) {
      errors.price = "Price is required";
      valid = false;
    } else if (isNaN(parseFloat(formData.price))) {
      errors.price = "Price must be a number";
      valid = false;
    }

    setFormErrors(errors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      let itemId: string;

      if (selectedItem) {
        // Update existing item
        await updateItem(
          selectedItem.id,
          {
            name: formData.name,
            description: formData.description,
            price: formData.price,
            hidden: formData.hidden,
          },
          image
        );
        itemId = selectedItem.id;
      } else {
        // Create new item
        if (!currentUser) {
          throw new Error("You must be logged in to create an item");
        }

        itemId = await createItem(
          formData.name,
          formData.description,
          image,
          formData.price,
          formData.hidden,
          currentUser.uid
        );
      }

      // Create or update Stripe product for this item
      if (!formData.hidden && parseFloat(formData.price) > 0) {
        try {
          await createOrUpdateStripeProductForItem(itemId);
        } catch (stripeError) {
          console.error("Failed to create Stripe product:", stripeError);
          // Continue even if Stripe product creation fails
        }
      }

      await loadItems();
      handleCloseDialog();
    } catch (err) {
      console.error("Failed to save item:", err);
      setError("Failed to save item. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (item: Item) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem) return;

    try {
      setLoading(true);
      await deleteItem(selectedItem.id);
      await loadItems();
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error("Failed to delete item:", err);
      setError("Failed to delete item. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Product Management</h1>
        <button
          onClick={() => handleOpenDialog()}
          className="flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md shadow-sm"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          New Product
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {items.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col h-full"
            >
              {item.imageURL ? (
                <img
                  src={item.imageURL}
                  alt={item.name}
                  className="h-48 w-full object-cover"
                />
              ) : (
                <div className="h-48 bg-gray-200 flex justify-center items-center">
                  <ShoppingBagIcon className="h-12 w-12 text-gray-400" />
                </div>
              )}

              <div className="p-4 flex-grow">
                <h2 className="text-xl font-semibold mb-2 text-gray-900">
                  {item.name}
                </h2>
                <div className="text-gray-600 mb-4 line-clamp-3">
                  {item.description}
                </div>

                <div className="space-y-1 text-sm">
                  <div className="flex items-center">
                    <span className="font-medium mr-2">Price:</span>
                    <span>${item.price}</span>
                  </div>

                  <div className="flex items-center">
                    <span className="font-medium mr-2">Status:</span>
                    <span
                      className={`${
                        item.hidden ? "text-gray-500" : "text-green-600"
                      }`}
                    >
                      {item.hidden ? "Hidden" : "Active"}
                    </span>
                  </div>

                  {item.stripeProductId && (
                    <div className="flex items-center">
                      <span className="font-medium mr-2">Stripe Product:</span>
                      <span className="text-gray-600">
                        {item.stripeProductId.substring(0, 10)}...
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-between">
                <button
                  onClick={() => handleOpenDialog(item)}
                  className="text-gray-700 hover:text-blue-600 flex items-center text-sm"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </button>

                <button
                  onClick={() => handleDeleteClick(item)}
                  className="text-red-600 hover:text-red-800 flex items-center text-sm"
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Item Dialog */}
      {openDialog && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  {selectedItem ? "Edit Product" : "Add New Product"}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className={`mt-1 block w-full rounded-md shadow-sm ${
                        formErrors.name
                          ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      }`}
                      required
                    />
                    {formErrors.name && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex flex-col">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product Image
                      </label>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm"
                          onClick={() =>
                            document.getElementById("file-upload")?.click()
                          }
                        >
                          {selectedItem && selectedItem.imageURL
                            ? "Change Image"
                            : "Upload Image"}
                        </button>
                        <input
                          id="file-upload"
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleFileChange}
                        />

                        {previewUrl && (
                          <img
                            src={previewUrl}
                            alt="Preview"
                            className="h-16 w-16 object-cover rounded-md"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="price"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Price
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="text"
                        id="price"
                        value={formData.price}
                        onChange={(e) =>
                          setFormData({ ...formData, price: e.target.value })
                        }
                        className={`block w-full pl-7 pr-12 rounded-md ${
                          formErrors.price
                            ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        }`}
                        placeholder="0.00"
                        inputMode="decimal"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">AUD</span>
                      </div>
                    </div>
                    {formErrors.price && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.price}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center h-full pt-6">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.hidden}
                        onChange={(e) =>
                          setFormData({ ...formData, hidden: e.target.checked })
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      <span className="ml-3 text-sm font-medium text-gray-700">
                        Hidden
                      </span>
                    </label>
                    <p className="ml-2 text-xs text-gray-500">
                      When hidden, the product will not be visible to users
                    </p>
                  </div>

                  <div className="col-span-1 sm:col-span-2">
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Description
                    </label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      rows={4}
                      className={`mt-1 block w-full rounded-md shadow-sm ${
                        formErrors.description
                          ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      }`}
                      required
                    />
                    {formErrors.description && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : selectedItem ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon
                      className="h-6 w-6 text-red-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div
                    className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left"
                    id="modal-title"
                  >
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Product
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete the product "
                        {selectedItem?.name}"? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleDeleteConfirm}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleDeleteCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;
